import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  Box,
  Button,
  TextField,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';

interface EventFormProps {
  contactId: string;
  eventId?: string; // Optional event ID for editing
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function EventForm({ contactId, eventId, onSuccess, onCancel }: EventFormProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    metadata: {
      start_date: '',
      end_date: '',
      location: '',
      type: 'tweet' as 'tweet' | 'in_person' | 'article' | 'meeting' | 'phone_call' | 'quant_update' | 'other',
      status: 'pending' as 'pending' | 'completed' | 'cancelled',
      notes: [] as string[],
      tags: [] as string[],
      is_read: false,
      read_action: null as 'thumbs_up' | 'ignore' | null
    }
  });

  // Load existing event data when editing
  useEffect(() => {
    if (eventId) {
      const loadEvent = async () => {
        try {
          const { data, error } = await supabase
            .from('events')
            .select('*')
            .eq('id', eventId)
            .single();

          if (error) throw error;

          if (data) {
            setFormData({
              title: data.title || '',
              description: data.description || '',
              metadata: {
                start_date: data.metadata?.start_date || '',
                end_date: data.metadata?.end_date || '',
                location: data.metadata?.location || '',
                type: data.metadata?.type || 'tweet',
                status: data.metadata?.status || 'pending',
                notes: data.metadata?.notes || [],
                tags: data.metadata?.tags || [],
                is_read: data.metadata?.is_read || false,
                read_action: data.metadata?.read_action || null
              }
            });
          }
        } catch (error) {
          console.error('Error loading event:', error);
        }
      };

      loadEvent();
    }
  }, [eventId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      if (eventId) {
        // Update existing event
        const { error } = await supabase
          .from('events')
          .update({
            title: formData.title,
            description: formData.description,
            metadata: formData.metadata
          })
          .eq('id', eventId);

        if (error) throw error;
      } else {
        // Create new event
        const { error } = await supabase
          .from('events')
          .insert({
            title: formData.title,
            description: formData.description,
            contact_id: contactId,
            user_id: user.id,
            metadata: formData.metadata
          });

        if (error) throw error;
      }
      
      onSuccess?.();
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            fullWidth
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.metadata.type}
              label="Type"
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, type: e.target.value as 'tweet' | 'in_person' | 'article' | 'meeting' | 'phone_call' | 'quant_update' | 'other' }
              })}
            >
              <MenuItem value="tweet">Tweet</MenuItem>
              <MenuItem value="in_person">In Person</MenuItem>
              <MenuItem value="article">Article</MenuItem>
              <MenuItem value="meeting">Meeting</MenuItem>
              <MenuItem value="phone_call">Phone Call</MenuItem>
              <MenuItem value="quant_update">Quant Update</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          {eventId && (
            <>
              <FormControl fullWidth>
                <InputLabel>Read Status</InputLabel>
                <Select
                  value={formData.metadata.is_read ? 'read' : 'unread'}
                  label="Read Status"
                  onChange={(e) => setFormData({
                    ...formData,
                    metadata: { 
                      ...formData.metadata, 
                      is_read: e.target.value === 'read',
                      read_action: e.target.value === 'read' ? (formData.metadata.read_action || null) : null
                    }
                  })}
                >
                  <MenuItem value="unread">Unread</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                </Select>
              </FormControl>

              {formData.metadata.is_read && (
                <FormControl fullWidth>
                  <InputLabel>Read Action</InputLabel>
                  <Select
                    value={formData.metadata.read_action || ''}
                    label="Read Action"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        metadata: { 
                          ...formData.metadata, 
                          read_action: value === '' ? null : (value === 'thumbs_up' ? 'thumbs_up' : 'ignore')
                        }
                      });
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="thumbs_up">👍 Thumbs Up</MenuItem>
                    <MenuItem value="ignore">Ignore</MenuItem>
                  </Select>
                </FormControl>
              )}
            </>
          )}

          <TextField
            label="Location"
            value={formData.metadata.location}
            onChange={(e) => setFormData({
              ...formData,
              metadata: { ...formData.metadata, location: e.target.value }
            })}
            fullWidth
          />

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Optional Date Information
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="datetime-local"
              value={formData.metadata.start_date}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, start_date: e.target.value }
              })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="datetime-local"
              value={formData.metadata.end_date}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, end_date: e.target.value }
              })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? (eventId ? 'Updating...' : 'Creating...') : (eventId ? 'Update Event' : 'Create Event')}
        </Button>
      </DialogActions>
    </form>
  );
} 